import React, { useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip } from 'chart.js';
import { fmtDec, MetricCard, GC, TC } from '../utils';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip);

function findSummary(commissionSummary, user) {
  return commissionSummary.find(s => s.name.toLowerCase().includes(user.name.split(' ')[0].toLowerCase()));
}
function findRate(commissionRates, user) {
  return commissionRates.find(r => r.name.toLowerCase().includes(user.name.split(' ')[0].toLowerCase()));
}
function findPayouts(payouts, user) {
  return payouts.filter(p => p.owner.toLowerCase().includes(user.name.split(' ')[0].toLowerCase()));
}

export function RepCommission({ data, user }) {
  const { commissionSummary = [], commissionRates = [] } = data;
  const summary = findSummary(commissionSummary, user);
  const rateRow = findRate(commissionRates, user);

  if (!summary || summary.commission === 0) {
    return (
      <div>
        <div className="rep-welcome"><h2>Commission summary</h2><p>Your commission breakdown for 2026.</p></div>
        <div className="metric-card"><div className="metric-label">Status</div><div className="metric-value">—</div><div className="metric-sub">No commission plan on your account</div></div>
      </div>
    );
  }

  const chartData = {
    labels: ['Commission earned', 'Paid out', 'Balance due'],
    datasets: [{ data: [summary.commission, summary.paid, summary.due], backgroundColor: ['#3ea4b1', '#1D9E75', summary.due > 0 ? '#E24B4A' : '#e5e7eb'], borderRadius: 6, borderSkipped: false }],
  };
  const opts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => '$' + ctx.parsed.y.toFixed(2) } } }, scales: { x: { grid: { display: false }, ticks: { color: TC, font: { size: 12 } } }, y: { grid: { color: GC }, ticks: { color: TC, font: { size: 11 }, callback: v => '$' + (v / 1000).toFixed(1) + 'k' } } } };

  return (
    <div>
      <div className="rep-welcome"><h2>Commission summary</h2><p>Your commission breakdown for 2026.</p></div>
      <div className="metrics">
        <MetricCard label="Commission earned" value={fmtDec(summary.commission)} sub={'On ' + fmtDec(summary.itemTotal) + ' in sales'} />
        <MetricCard label="Paid out" value={fmtDec(summary.paid)} sub={Math.round(summary.paid / summary.commission * 100) + '% of earned'} valueStyle={{ color: 'var(--green)' }} />
        <MetricCard label="Balance due" value={fmtDec(summary.due)} sub={summary.due > 0 ? 'Pending payment' : 'Fully paid'} valueStyle={summary.due > 0 ? { color: 'var(--red)' } : {}} />
      </div>
      <div className="chart-card" style={{ marginTop: 20 }}>
        <div className="chart-card-title">Commission status</div>
        <div className="chart-card-sub">Earned vs. paid vs. balance due</div>
        <div className="chart-wrap" style={{ height: 180 }}><Bar data={chartData} options={opts} /></div>
      </div>
    </div>
  );
}

export function RepCommissionRate({ data, user }) {
  const { commissionRates = [], commissionSummary = [] } = data;
  const rateRow = findRate(commissionRates, user);
  const summary = findSummary(commissionSummary, user);
  const myRate = rateRow?.rate || 0;

  const activeRates = commissionRates.filter(r => r.rate > 0);
  const isMe = (r) => r.name.toLowerCase().includes(user.name.split(' ')[0].toLowerCase());
  const labels = activeRates.map((r, i) => isMe(r) ? 'You' : 'Rep ' + (i + 1));
  const bgColors = activeRates.map(r => isMe(r) ? '#3ea4b1' : '#e5e7eb');

  const chartData = { labels, datasets: [{ data: activeRates.map(r => r.rate), backgroundColor: bgColors, borderRadius: 6, borderSkipped: false }] };
  const opts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ctx.parsed.y + '%' } } }, scales: { x: { grid: { display: false }, ticks: { color: TC, font: { size: 12 } } }, y: { grid: { color: GC }, ticks: { color: TC, font: { size: 11 }, callback: v => v + '%' }, max: 10 } } };

  return (
    <div>
      <div className="rep-welcome"><h2>Your commission rate</h2><p>How your commission is structured.</p></div>
      <div className="metrics">
        <MetricCard label="Item sales total" value={summary ? fmtDec(summary.itemTotal) : '—'} sub="Commissionable revenue" />
        <MetricCard label="Commission earned" value={summary ? fmtDec(summary.commission) : '—'} sub="2026 YTD" />
        <MetricCard label="Effective rate" value={summary?.itemTotal > 0 ? (summary.commission / summary.itemTotal * 100).toFixed(2) + '%' : '—'} sub="Actual vs. stated rate" />
      </div>
      <div className="chart-card" style={{ marginTop: 20 }}>
        <div className="chart-card-title">How your rate compares</div>
        <div className="chart-card-sub">Your rate vs. other active reps (anonymized)</div>
        <div className="chart-wrap" style={{ height: 180 }}><Bar data={chartData} options={opts} /></div>
      </div>
    </div>
  );
}

export function RepPayouts({ data, user }) {
  const { payouts = [], repData = {} } = data;
  const myPayouts = findPayouts(payouts, user);
  const myPeriods = repData[user.repId] || [];
  const [expandedKey, setExpandedKey] = useState(null);

  if (myPayouts.length === 0) {
    return (
      <div>
        <div className="rep-welcome"><h2>Your payouts</h2><p>All commission payments made to you in 2026.</p></div>
        <div className="metric-card"><div className="metric-label">Status</div><div className="metric-value">—</div><div className="metric-sub">No payouts recorded yet</div></div>
      </div>
    );
  }

  const total = myPayouts.reduce((s, p) => s + p.amount, 0);
  const largest = myPayouts.reduce((a, b) => b.amount > a.amount ? b : a);
  const last = myPayouts[myPayouts.length - 1];

  let running = 0;
  const cumData = myPayouts.map(p => { running += p.amount; return running; });
  const timeData = {
    labels: myPayouts.map(p => p.date),
    datasets: [{ data: cumData, borderColor: '#3ea4b1', backgroundColor: 'rgba(62,164,177,0.08)', pointBackgroundColor: '#3ea4b1', pointRadius: 4, tension: 0.3, fill: true }],
  };
  const opts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => '$' + ctx.parsed.y.toFixed(2) } } }, scales: { x: { grid: { display: false }, ticks: { color: TC, font: { size: 10 }, maxRotation: 45 } }, y: { grid: { color: GC }, ticks: { color: TC, font: { size: 11 }, callback: v => '$' + (v / 1000).toFixed(1) + 'k' } } } };

  // Build a lookup: payout date → jobs from repData periods
  const periodByDate = {};
  myPeriods.forEach(p => { periodByDate[p.date] = p.jobs || []; });

  return (
    <div>
      <div className="rep-welcome"><h2>Your payouts</h2><p>All commission payments made to you in 2026.</p></div>
      <div className="metrics">
        <MetricCard label="Total received" value={fmtDec(total)} sub="2026 YTD" />
        <MetricCard label="Payments made" value={myPayouts.length} sub="Pay periods" />
        <MetricCard label="Largest payment" value={fmtDec(largest.amount)} sub={largest.date} />
        <MetricCard label="Most recent" value={fmtDec(last.amount)} sub={last.date} />
      </div>

      <div className="chart-card" style={{ marginTop: 20, marginBottom: 20 }}>
        <div className="chart-card-title">Payouts over time</div>
        <div className="chart-card-sub">Cumulative payments received</div>
        <div className="chart-wrap" style={{ height: 180 }}><Line data={timeData} options={opts} /></div>
      </div>

      <div className="table-card">
        <div className="table-header"><span className="table-header-title">Payout history</span></div>
        <div style={{ padding: '8px 0' }}>
          {[...myPayouts].reverse().map((p, i) => {
            const key = p.date + i;
            const isExp = expandedKey === key;
            const jobs = periodByDate[p.date] || [];
            return (
              <div key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <div
                  onClick={() => setExpandedKey(isExp ? null : key)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', cursor: 'pointer', background: isExp ? 'var(--surface2)' : 'transparent', userSelect: 'none' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 4, background: 'var(--brand-light)', color: 'var(--brand)', fontSize: 10, fontWeight: 700, flexShrink: 0, transform: isExp ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>▼</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text2)' }}>{p.date}</div>
                    <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{p.notes}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {jobs.length > 0 && (
                      <span style={{ fontSize: 11, color: 'var(--brand)', fontWeight: 500, background: 'var(--brand-light)', padding: '2px 8px', borderRadius: 20 }}>
                        {jobs.length} order{jobs.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    <div style={{ fontFamily: 'var(--mono)', fontWeight: 500, fontSize: 14 }}>${p.amount.toFixed(2)}</div>
                  </div>
                </div>
                {isExp && (
                  <div style={{ background: 'var(--surface2)', borderTop: '1px solid var(--border)', padding: '0 20px 12px' }}>
                    {jobs.length > 0 ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Order #</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Order nickname</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text3)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Payment date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobs.map((job, j) => (
                            <tr key={j} style={{ borderTop: '1px solid var(--border)' }}>
                              <td style={{ padding: '8px 8px', fontFamily: 'var(--mono)', fontWeight: 500, color: 'var(--brand)' }}>{job.orderNumber}</td>
                              <td style={{ padding: '8px 8px', color: 'var(--text)', maxWidth: 300, whiteSpace: 'normal' }}>{job.nickname}</td>
                              <td style={{ padding: '8px 8px', fontFamily: 'var(--mono)', textAlign: 'right', color: 'var(--text3)', fontSize: 11 }}>{job.paymentDate}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ padding: '12px 8px', fontSize: 12, color: 'var(--text3)' }}>No orders matched to this pay period.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


export default RepCommission;
